import { FormEvent, useEffect, useState } from 'react';
import type { User, EcosystemProfileSetting } from '@frontend/types';
import { authClient } from '@frontend/api-clients/authClient';
import { fetchProfileEcosystems, updateProfileEcosystem } from '@frontend/api-clients/profileEcosystemsClient';

interface EcosystemSettingsPanelProps {
	user: User | null;
}


export default function EcosystemSettingsPanel({ user }: EcosystemSettingsPanelProps) {
	const [ecosystems, setEcosystems] = useState<EcosystemProfileSetting[]>([]);
	const [loading, setLoading] = useState(true);
	const [savingId, setSavingId] = useState<string | null>(null);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');

	useEffect(() => {
		const loadProfiles = async () => {
			setLoading(true);
			setError('');
			setSuccess('');

			try {
				const token = authClient.getToken();
				if (!token) {
					setError('You must be signed in to view ecosystem settings.');
					setLoading(false);
					return;
				}

				const list = await fetchProfileEcosystems(token);
				setEcosystems(list);
			} catch (loadError) {
				setError(loadError instanceof Error ? loadError.message : 'Failed to load ecosystem settings');
			} finally {
				setLoading(false);
			}
		};

		loadProfiles();
	}, [user]);

	const updateField = (ecosystemId: string, field: 'displayName' | 'replyEmail', value: string) => {
		setEcosystems((current) =>
			current.map((ecosystem) =>
				ecosystem.id === ecosystemId ? { ...ecosystem, [field]: value } : ecosystem
			)
		);
	};

	const saveEcosystemSettings = async (event: FormEvent, ecosystem: EcosystemProfileSetting) => {
		event.preventDefault();
		setError('');
		setSuccess('');
		setSavingId(ecosystem.id);

		try {
			const token = authClient.getToken();
			if (!token) throw new Error('You must be signed in to save ecosystem settings.');

			const data = await updateProfileEcosystem(ecosystem.id, token, {
				displayName: ecosystem.displayName,
				replyEmail: ecosystem.replyEmail,
			});
			setEcosystems((current) =>
				current.map((entry) => (entry.id === ecosystem.id ? data : entry))
			);
			setSuccess(`Saved settings for ${ecosystem.name}.`);
		} catch (saveError) {
			setError(saveError instanceof Error ? saveError.message : 'Failed to save ecosystem settings');
		} finally {
			setSavingId(null);
		}
	};

	return (
		<div className="tree-dashboard-panel">
			<h2>Ecosystem Settings</h2>
			<p className="tree-dashboard-muted">
				Manage the display name and reply email shown to members of each ecosystem.
			</p>

			{error ? <div className="settings-message settings-error">{error}</div> : null}
			{success ? <div className="settings-message settings-success">{success}</div> : null}

			{loading ? (
				<p className="tree-dashboard-muted">Loading ecosystem settings…</p>
			) : ecosystems.length === 0 ? (
				<p className="tree-dashboard-muted">No ecosystem memberships found for your account.</p>
			) : (
				<div className="settings-panel-grid">
					{ecosystems.map((ecosystem) => (
						<form
							key={ecosystem.id}
							className="ecosystem-card settings-card"
							onSubmit={(event) => saveEcosystemSettings(event, ecosystem)}
						>
							<h3 className="ecosystem-title">{ecosystem.name}</h3>
							<p className="ecosystem-description">Set profile fields visible within this ecosystem.</p>

							<label className="settings-label" htmlFor={`panel-${ecosystem.id}-display`}>
								Display Name
							</label>
							<input
								id={`panel-${ecosystem.id}-display`}
								type="text"
								value={ecosystem.displayName}
								maxLength={120}
								onChange={(event) => updateField(ecosystem.id, 'displayName', event.target.value)}
							/>
							{ecosystem.isDisplayNameFallback ? (
								<p className="settings-hint">Using your global profile value.</p>
							) : null}

							<label className="settings-label" htmlFor={`panel-${ecosystem.id}-reply`}>
								Reply Email
							</label>
							<input
								id={`panel-${ecosystem.id}-reply`}
								type="email"
								value={ecosystem.replyEmail}
								maxLength={254}
								onChange={(event) => updateField(ecosystem.id, 'replyEmail', event.target.value)}
							/>
							{ecosystem.isReplyEmailFallback ? (
								<p className="settings-hint">Using your global profile value.</p>
							) : null}

							<div className="settings-actions">
								<button type="submit" className="btn btn-primary" disabled={savingId === ecosystem.id}>
									{savingId === ecosystem.id ? 'Saving…' : 'Save'}
								</button>
								{ecosystem.updatedAt ? (
									<span className="settings-updated">
										Updated {new Date(ecosystem.updatedAt).toLocaleString()}
									</span>
								) : null}
							</div>
						</form>
					))}
				</div>
			)}
		</div>
	);
}
